import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { actions as userActions } from "store/users";

import Dialog from "components/Dialog";
import Button from "components/Button";

import classes from "./styles.module.scss";

const { saveUserMeta } = userActions;

export const LEGAL_UPDATE_VERSION_ID = "1.0.0";

const LEGAL_LINKS = [
    { label: "Terms and Conditions", href: "/resources/terms" },
    { label: "Privacy Policy", href: "/resources/privacy" },
    { label: "Software Licensing Agreement", href: "/resources/licence" },
];

/**
 * One-time splash after a major release asking users to review updated legal docs.
 * Acknowledgement is stored on userMeta.legalUpdatesAcknowledged[versionId].
 */
const LegalUpdateNotice = ({ versionId = LEGAL_UPDATE_VERSION_ID }) => {
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const uid = useSelector((state) => state.user?.uid);
    const agreedToTerms = useSelector((state) => state.user?.userMeta?.agreedToTerms);
    const isEmailConfirmed = useSelector((state) => state.user?.isEmailConfirmed);
    const legalUpdatesAcknowledged = useSelector((state) => state.user?.userMeta?.legalUpdatesAcknowledged || {});

    const alreadyAcknowledged = Boolean(legalUpdatesAcknowledged[versionId]);
    const isOpen = Boolean(uid && agreedToTerms && isEmailConfirmed && !alreadyAcknowledged);

    const handleAgree = async () => {
        if (!uid || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await dispatch(
                saveUserMeta({
                    uid,
                    userMeta: {
                        legalUpdatesAcknowledged: {
                            ...legalUpdatesAcknowledged,
                            [versionId]: true,
                        },
                    },
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} closeActions={() => {}} classes={{ paperClass: classes.paper }} disableEscapeKeyDown>
            <div className={classes.content}>
                <h2>Updated Legal Documents</h2>
                <p>
                    With the release of Volta SNAP version {versionId}, we have updated our Terms and Conditions,
                    Privacy Policy, and Software Licensing Agreement to reflect new features and changes to the app.
                </p>
                <p>Please review the updated documents:</p>
                <ul className={classes.linkList}>
                    {LEGAL_LINKS.map(({ label, href }) => (
                        <li key={href}>
                            <a href={href} target="_blank" rel="noopener noreferrer">
                                {label}
                            </a>
                        </li>
                    ))}
                </ul>
                <p>
                    Among other things, these updates address new paid features and the collection of anonymized upgrade
                    measure costs entered by users.
                </p>
                <p>
                    By continuing to use Volta SNAP, you acknowledge that you have had the opportunity to review these
                    documents and agree to be bound by the updated Terms and Conditions, Privacy Policy, and Software
                    Licensing Agreement.
                </p>
                <div className={classes.footer}>
                    <Button onClick={handleAgree} disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "I Agree"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

export default LegalUpdateNotice;
